/**
 * The chat pane's horizontal proportions: how wide the message column runs, how
 * much room the artifacts index takes beside it, and the gutter that keeps the
 * two apart.
 *
 * These three are one measurement, not three independent knobs — the column is
 * centred in whatever the index leaves behind, so moving any one of them moves
 * the balance of all of them. They live together here for that reason.
 *
 * Set against Notion's chat, measured at a ~1860px viewport:
 *
 *   sidebar 329px │ gutter 128 │ column 860px │ gutter 135 │ artifacts 393px
 *
 * The column sits centred between the sidebar and the artifacts rail, with
 * gutters that match each other. Ours did not: the column was centred in the
 * *whole* pane, which at `max-w-5xl` left a 327px gutter on the left and 17px
 * on the right — the index card was not beside the conversation so much as
 * pressed against it, and its titles truncated to make room ("Marketing —
 * Synthetic …") while a third of the pane sat empty on the other side.
 *
 * Landing on Notion's balance at that viewport:
 *
 *   sidebar 288px │ gutter 154 │ column 896px │ gutter 154 │ artifacts 352px
 *
 * The sidebar is deliberately untouched. Ours is 18rem against Notion's 329px,
 * but Notion's is user-resized from a 240px default — ours already sits between
 * the two, and it is the app's global nav rather than anything about chat.
 *
 * One thing to know before reading any `rem` below as the pixel figure beside
 * it: the app sets `html { font-size: 0.8125rem }`, a 13px root, so every `rem`
 * here resolves to 81% of what Tailwind's own scale suggests. The measurements
 * above were taken against a 16px root and the constants were written to match
 * them, which means they all shipped ~19% under the design they document —
 * `max-w-4xl` is 728px, not the 896px it was chosen for, and the sidebar is
 * 234px rather than 288px.
 *
 * The card and its gutter scaled down together, so their relationship still
 * holds. The column did not: it is capped independently, and a reading column
 * 168px under its intended width is simply a wider empty margin on both sides
 * of the conversation. That is what {@link CHAT_COLUMN_MAX_WIDTH} corrects.
 */

/**
 * Cap on the chat's message column and composer. 896px, against the app's
 * general `max-w-5xl` (1024px) content cap — the chat is the one surface with a
 * rail beside it, and 1024 left no room for one.
 *
 * Spelled as `69rem` rather than `max-w-4xl` because at this app's 13px root
 * that named step is 728px, not the 896 this was chosen to be. It stays in
 * `rem` rather than becoming `896px` so the column still travels with the rest
 * of the layout if the root ever moves.
 *
 * Only ever a cap: on a pane too narrow to grant it the column shrinks as it
 * always did, so nothing below `xl` changes.
 */
export const CHAT_COLUMN_MAX_WIDTH = 'max-w-[69rem]';

/**
 * The artifacts index card's width. 352px, up from 272px: at the old width the
 * card was 15% of the viewport against Notion's 21%, and every title in it
 * ellipsized.
 */
export const ARTIFACTS_INDEX_WIDTH = 'w-[22rem]';

/**
 * Room the chat gives up on its right so the index card sits *beside* the
 * conversation rather than over it — the card's own width plus its `right-3`
 * offset and a hair.
 *
 * Published as a custom property rather than applied as padding, because the
 * chat is generic: it renders in a standalone page as readily as beside this
 * panel, and it must not import the panel to find out which. The panel sets the
 * property on an ancestor; the chat's own boxes read it through
 * {@link CHAT_RAIL_GUTTER} and get 0 wherever nothing set it.
 *
 * That indirection is also what keeps the scrollbar where it belongs. Padding an
 * ancestor of the conversation shrinks the scroll container itself, and a
 * scrollbar is painted at its own container's edge — so the bar detached from
 * the window and floated in the middle of the pane, hard against the card. Only
 * the boxes *inside* the scroll container may give up the gutter; the scroller
 * stays full width, and its bar stays at the far right of the window where
 * Notion's is.
 *
 * Paired with {@link ARTIFACTS_INDEX_WIDTH}: change one and this follows, or the
 * card starts overlapping the column again.
 *
 * Held back to `xl` (1280px). Below that the pane is too narrow to give up
 * 368px of it without crushing the conversation, and the column is nowhere near
 * its cap anyway — the card overlays, which is what it did everywhere before.
 */
export const ARTIFACTS_INDEX_GUTTER = 'xl:[--chat-rail-gutter:23rem]';

/**
 * Reads the gutter {@link ARTIFACTS_INDEX_GUTTER} published, defaulting to none.
 *
 * Goes on the conversation's scroll element itself (`ConversationContent`'s
 * `scrollClassName`, not `Conversation`) and on the composer's wrapper — never
 * on an *ancestor* of the scroller. See {@link ARTIFACTS_INDEX_GUTTER}.
 *
 * The distinction is the whole point and it is easy to get backwards, because
 * `Conversation` looks like the scroller and isn't: `StickToBottom` renders the
 * element that actually scrolls one level in, sized `width: 100%` of whatever
 * the root leaves it. Padding the root therefore *narrows the scroller*, and a
 * scrollbar is painted at its own element's edge — so the bar detached from the
 * window and floated 368px in, hard against the index card, with the chat still
 * scrolling under it.
 *
 * Padding the scroll element instead does what the rail wants: the bar stays at
 * the border-box edge (padding sits inside it, and `box-sizing: border-box`
 * keeps the element full width), while the centred column inside gives up the
 * room and slides left of the card.
 */
export const CHAT_RAIL_GUTTER =
  'pr-[var(--chat-rail-gutter,0px)] transition-[padding] duration-[320ms] ease-out motion-reduce:transition-none';

/**
 * The gutter animates rather than snapping, at the `duration-[320ms] ease-out`
 * the artifact panel uses for its own width — opening the index card should
 * push the conversation aside the same way opening an artifact does, because
 * from the reader's side it is the same event: something arrived on the right
 * and the column made room.
 *
 * The transition rides on the *consuming* element's `padding-right`, not on the
 * custom property. An unregistered custom property has no interpolable type and
 * changes discretely, but that is not what is being animated here — the
 * property change resolves to a new computed `padding-right` on this element,
 * and a transition on padding animates that. Registering the property with
 * `@property` would be the fix if the variable itself ever had to animate; it
 * does not.
 */
